import { formatOData } from '@helpers';
import type { ApiConfig, ODataParams } from '@api/types';
import { typedMethodPost, typedMethodGet } from '@api/utils';
import type {
  MachineEventSearchResponseDto,
  NcFileSearchResponseDto,
  NcFileDto,
  MachineEventsTimelineResponseDto,
  MachineEventsTimelineRequestDto,
  MachineEventsTimelineDetailsRequestDto,
  MachineEventsTimelineDetailsResponseDto,
  MachineEventDto,
  AddProductionInformationRequestDto,
  IgnoreProductionInformationRequestDto,
  SetChangeProductionInformationRequestDto,
  SetSetupProductionInformationRequestDto,
  RemoveProductionInformationRequestDto,
} from '../../generated/models';
import type {
  SearchMachineEventsRequest as SearchMachineEventsRequestLocal,
  SearchNcFilesRequest as SearchNcFilesRequestLocal,
} from './types';

// Domain types
import type {
  ManufactureMachineEventSearchResponse,
  ManufactureNcFileSearchResponse,
  NcFile,
  MachineEventsTimelineResponse,
  MachineEventsTimelineRequest,
  MachineEventsTimelineDetailsRequest,
  MachineEventsTimelineDetailsResponse,
  MachineEvent,
  AddProductionInformationRequest,
  IgnoreProductionInformationRequest,
  SetChangeProductionInformationRequest,
  SetSetupProductionInformationRequest,
  RemoveProductionInformationRequest,
  SearchMachineEventsRequest,
  SearchNcFilesRequest,
} from '../../domain/types';

// Mappers
import {
  toManufactureMachineEventSearchResponse,
  toManufactureNcFileSearchResponse,
  toNcFileArray,
  toMachineEventsTimelineResponse,
  toMachineEventsTimelineDetailsResponse,
  toMachineEventArray,
} from '../../domain/mappers';

export default ({ baseURL, baseURLoData }: ApiConfig) => ({
  addProductionInformation: typedMethodPost<AddProductionInformationRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/productionInformation/addProductionInformation`, {
        method: 'POST',
        ...data,
      })
  ),
  ignoreProductionInformation: typedMethodPost<IgnoreProductionInformationRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/productionInformation/ignoreProductionInformation`, {
        method: 'POST',
        ...data,
      })
  ),
  setChangeProductionInformation: typedMethodPost<SetChangeProductionInformationRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/productionInformation/setChangeProductionInformation`, {
        method: 'POST',
        ...data,
      })
  ),
  setSetupProductionInformation: typedMethodPost<SetSetupProductionInformationRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/productionInformation/setSetupProductionInformation`, {
        method: 'POST',
        ...data,
      })
  ),
  removeProductionInformation: typedMethodPost<RemoveProductionInformationRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/productionInformation/removeProductionInformation`, {
        method: 'POST',
        ...data,
      })
  ),
  searchMachineEvents: typedMethodPost<
    SearchMachineEventsRequestLocal,
    MachineEventSearchResponseDto,
    ManufactureMachineEventSearchResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/manufacture/searchMachineEvents`, {
        method: 'POST',
        ...data,
      }),
    toManufactureMachineEventSearchResponse
  ),
  searchNcFiles: typedMethodPost<SearchNcFilesRequestLocal, NcFileSearchResponseDto, ManufactureNcFileSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v1/search/ncFiles`, {
        method: 'POST',
        ...data,
      }),
    toManufactureNcFileSearchResponse
  ),
  ncFile: typedMethodGet<ODataParams, NcFileDto[], NcFile[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/ncFile?${formatOData(data.params)}`, {
        method: 'GET',
        headers: data.headers,
      }),
    toNcFileArray
  ),
  machineEventTimeline: typedMethodPost<
    MachineEventsTimelineRequestDto,
    MachineEventsTimelineResponseDto,
    MachineEventsTimelineResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/machines/eventsTimeline`, {
        method: 'POST',
        ...data,
      }),
    toMachineEventsTimelineResponse
  ),
  machineEventTimelineDetail: typedMethodPost<
    MachineEventsTimelineDetailsRequestDto,
    MachineEventsTimelineDetailsResponseDto,
    MachineEventsTimelineDetailsResponse
  >(
    (data) =>
      fetch(`${baseURL}/v2/machines/eventsTimelineDetails`, {
        method: 'POST',
        ...data,
      }),
    toMachineEventsTimelineDetailsResponse
  ),
  machineEvent: typedMethodGet<ODataParams, MachineEventDto[], MachineEvent[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/MachineEvent?${formatOData(data.params)}`, {
        method: 'GET',
        headers: data.headers,
      }),
    toMachineEventArray
  ),
});

// Re-export domain types for consumers
export type {
  ManufactureMachineEventSearchResponse,
  ManufactureNcFileSearchResponse,
  NcFile,
  MachineEventsTimelineResponse,
  MachineEventsTimelineRequest,
  MachineEventsTimelineDetailsRequest,
  MachineEventsTimelineDetailsResponse,
  MachineEvent,
  AddProductionInformationRequest,
  IgnoreProductionInformationRequest,
  SetChangeProductionInformationRequest,
  SetSetupProductionInformationRequest,
  RemoveProductionInformationRequest,
  SearchMachineEventsRequest,
  SearchNcFilesRequest,
};
